'use client';

import Kols from './case-kols';
import TestingData from './case-testing-data';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import CustomLink from '@/components/ui/common/custom-link';
import { useGSAP } from '@gsap/react';
import initAnimation from './animations/case-animation';
import { useLocale } from '@/components/providers/context/locale-context';
import { JSection, JContainer } from '@/components/ui/layout';
import {
    buildStaticAssetPath,
    getStaticAssetUrl,
} from '@/app/lib/utils/static-asset-url';

function Quotation({ direction }: Readonly<{ direction: 'left' | 'right' }>) {
    return direction === 'left' ? (
        <svg
            className="h-[24px] w-[24px] sm:h-[48px] sm:w-[48px]"
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            fill="#6C49FF"
        >
            <path d="M585.813333 859.733333h347.306667V512h-176.64c0-97.706667 78.933333-177.066667 176.64-177.066667v-170.666666c-192 0-347.306667 155.733333-347.306667 347.733333v347.733333zM90.88 859.733333h347.306667V512H261.546667c0-97.706667 78.933333-177.066667 176.64-177.066667v-170.666666c-192 0-347.306667 155.733333-347.306667 347.733333v347.733333z"></path>
        </svg>
    ) : (
        <svg
            className="h-[24px] w-[24px] sm:h-[48px] sm:w-[48px]"
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            fill="#6C49FF"
        >
            <path d="M438.186667 164.266667H90.88V512h176.64c0 97.706667-78.933333 177.066667-176.64 177.066667v170.666666c192 0 347.306667-155.733333 347.306667-347.733333V164.266667zM933.12 164.266667h-347.306667V512h176.64c0 97.706667-78.933333 177.066667-176.64 177.066667v170.666666c192 0 347.306667-155.733333 347.306667-347.733333V164.266667z"></path>
        </svg>
    );
}

function Case() {
    const { t } = useTranslation('case');
    const locale = useLocale();
    const rootRef = useRef<HTMLDivElement>(null);
    const readTexts: string[] = t('readTexts', {
        returnObjects: true,
    }) as string[];
    const readTextsBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('bg-readtexts.webp'),
    );

    useGSAP(
        () => {
            initAnimation();
        },
        { scope: rootRef },
    );

    return (
        <JSection
            ref={rootRef}
            className="flex flex-col items-center bg-white pt-[80px] sm:pt-[120px]"
            size="full"
            padding="none"
            spacing="none"
        >
            <div className="flex w-full flex-col items-center gap-[60px]">
                <h2
                    className={clsx(
                        'j-h2 w-full text-center text-[#06060A] sm:flex sm:flex-col',
                    )}
                >
                    <span>{t('question.firstLine')}</span>
                    <span
                        style={{
                            background:
                                'linear-gradient(90deg, #6B48FF -0.07%, #E7A9FD 99.93%)',
                            WebkitTextFillColor: 'transparent',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                        }}
                    >
                        {t('question.secondLine')}
                    </span>
                </h2>
            </div>

            <div
                className="mt-[60px] flex w-full items-center justify-center bg-cover bg-center bg-no-repeat sm:mt-[120px]"
                style={{
                    backgroundImage: `url(${readTextsBackgroundImageUrl})`,
                }}
            >
                <JContainer
                    size="lg"
                    padding="none"
                    className={clsx('j-h5 flex justify-center')}
                >
                    <div className="flex w-4/5 flex-col px-[37.5px] text-center">
                        <h3 className={clsx('j-h3 mb-[32px]')}>
                            {t('readTextsTitle')}
                        </h3>

                        <div className="relative">
                            <div className="absolute left-[-32px] top-[-12px] sm:left-[-72px] sm:top-[-24px]">
                                <Quotation direction="left" />
                            </div>
                            {readTexts.map((text, index) => {
                                const key = `read-text-${index}`;
                                return (
                                    <span
                                        key={key}
                                        style={{
                                            opacity: 0.2,
                                        }}
                                        className="read-text-span"
                                    >
                                        {text}
                                    </span>
                                );
                            })}
                            <div className="absolute bottom-[-12px] right-[-32px] sm:bottom-[-24px] sm:right-[-72px]">
                                <Quotation direction="right" />
                            </div>
                        </div>
                    </div>
                </JContainer>
            </div>

            <JContainer
                size="lg"
                padding="none"
                className="mt-[90px] flex h-auto flex-col pb-[60px] sm:mt-[calc(var(--simulaix-vh,100svh)*0.1)] sm:pb-[100px]"
            >
                <TestingData locale={locale} />
            </JContainer>

            <div className="flex w-full max-w-full items-center justify-center bg-[#E5E7E9]">
                <Kols />
            </div>

            <div
                className={clsx(
                    'j-h4 mt-0 flex w-full flex-col items-center justify-center py-[60px] text-center text-[#000000] sm:py-[calc(var(--simulaix-vh,100svh)*0.1)]',
                )}
            >
                <h3>{t('worry.question')}</h3>
                <div>
                    {t('worry.see.part1')}
                    <CustomLink
                        href={'/' + locale + '/blog/model-autophagy'}
                        iconSize={20}
                        fill="#000000"
                    >
                        {t('worry.see.part2')}
                    </CustomLink>
                    {t('worry.see.part3')}
                </div>
            </div>
        </JSection>
    );
}

export default Case;
